/**
 * S1 — Global Trial Banner
 *
 * Full-width bar below OfflineBanner (44px). Trigger: nearest-expiry flag.
 * Dismiss rules: healthy = 24h, warning/critical = session, expired = never.
 *
 * Desktop 1280 · Admin:     "AI Scribe trial ends in 12 days."  [Upgrade →] [×]
 * Desktop 1280 · Non-admin: "AI Scribe trial ends in 12 days."  [Notify admin] [×]
 * Multi-feature:            "3 trial features end this week."    [View details] [×]
 * Mobile 390:               "AI Scribe · 12 days left"           [Upgrade] [×]
 *
 * Figma: S1 — Global Trial Banner
 */
import React, { useState, useEffect } from "react";
import type { FeatureFlagState, Severity } from "../../hooks/useFeatureFlags";

interface TrialBannerProps {
  nearestExpiry: FeatureFlagState | null;
  expiredFlags: FeatureFlagState[];
  trialsEndingThisWeek: number;
  severity: Severity;
  isAdmin: boolean;
  onUpgradeClick: () => void;
  onViewDetails: () => void;
  onNotifyAdmin: () => void;
}

const DISMISS_KEY_PREFIX = "trial_banner_dismissed_";

export const TrialBanner: React.FC<TrialBannerProps> = ({
  nearestExpiry,
  expiredFlags,
  trialsEndingThisWeek,
  severity,
  isAdmin,
  onUpgradeClick,
  onViewDetails,
  onNotifyAdmin,
}) => {
  const [dismissed, setDismissed] = useState(false);

  // Check if previously dismissed (localStorage for healthy=24h, sessionStorage for warning/critical)
  useEffect(() => {
    if (severity === "expired") { setDismissed(false); return; }
    if (!severity) return;

    const key = DISMISS_KEY_PREFIX + severity;
    if (severity === "healthy") {
      const stored = localStorage.getItem(key);
      if (stored && Date.now() - parseInt(stored, 10) < 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    } else {
      if (sessionStorage.getItem(key)) setDismissed(true);
    }
  }, [severity]);

  if (!severity || dismissed) return null;

  // No banner if nothing to show
  const hasExpired = expiredFlags.length > 0;
  const hasTrials = nearestExpiry !== null;
  if (!hasExpired && !hasTrials) return null;

  const handleDismiss = () => {
    if (severity === "expired") return; // can't dismiss expired
    setDismissed(true);
    const key = DISMISS_KEY_PREFIX + severity;
    if (severity === "healthy") {
      localStorage.setItem(key, String(Date.now()));
    } else {
      sessionStorage.setItem(key, "1");
    }
  };

  // Determine content
  let bgClass: string;
  let textClass: string;
  let message: string;
  let ctaLabel: string;
  let ctaAction: () => void;
  let showDismiss = true;

  if (severity === "expired") {
    bgClass = "bg-red-600";
    textClass = "text-white";
    const featureName = expiredFlags[0]?.display_label || "Your trial";
    message = `Your ${featureName} trial has ended.`;
    ctaLabel = isAdmin ? "Upgrade now" : "Notify admin";
    ctaAction = isAdmin ? onUpgradeClick : onNotifyAdmin;
    showDismiss = false;
  } else if (trialsEndingThisWeek > 1) {
    // Multi-feature variant
    bgClass = severity === "critical" ? "bg-red-50 border-b border-red-200" : "bg-amber-50 border-b border-amber-200";
    textClass = severity === "critical" ? "text-red-700" : "text-amber-800";
    message = `${trialsEndingThisWeek} trial features end this week.`;
    ctaLabel = "View details";
    ctaAction = onViewDetails;
  } else if (nearestExpiry) {
    const days = nearestExpiry.days_left ?? 0;
    if (severity === "critical") {
      bgClass = "bg-red-50 border-b border-red-200";
      textClass = "text-red-700";
    } else if (severity === "warning") {
      bgClass = "bg-amber-50 border-b border-amber-200";
      textClass = "text-amber-800";
    } else {
      bgClass = "bg-slate-50 border-b border-slate-200";
      textClass = "text-slate-700";
    }
    message = `${nearestExpiry.display_label} trial ends in ${days} day${days !== 1 ? "s" : ""}.`;
    ctaLabel = isAdmin ? "Upgrade →" : "Notify admin";
    ctaAction = isAdmin ? onUpgradeClick : onNotifyAdmin;
  } else {
    return null;
  }

  return (
    <div className={`w-full h-10 sm:h-11 flex items-center justify-between px-3 sm:px-6 ${bgClass} z-30`}>
      {/* Message */}
      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
        <span className="text-xs sm:text-sm flex-shrink-0">⏳</span>
        {/* Desktop */}
        <span className={`text-sm font-medium ${textClass} hidden sm:inline truncate`}>
          {message}
        </span>
        {/* Mobile — compact */}
        <span className={`text-[11px] font-medium ${textClass} sm:hidden truncate`}>
          {nearestExpiry
            ? `${nearestExpiry.display_label} · ${nearestExpiry.days_left ?? 0}d left`
            : message}
        </span>
      </div>

      {/* CTA + Dismiss */}
      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
        <button
          onClick={ctaAction}
          className={`text-[10px] sm:text-xs font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-md transition-colors ${
            severity === "expired"
              ? "bg-white text-red-700 hover:bg-red-50"
              : "text-blue-700 hover:text-blue-900 hover:bg-blue-50"
          }`}
        >
          {ctaLabel}
        </button>
        {showDismiss && (
          <button
            onClick={handleDismiss}
            className={`p-0.5 sm:p-1 rounded-md hover:bg-black/5 ${textClass}`}
            aria-label="Dismiss"
          >
            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default TrialBanner;
